"use client";

import NextImage from "next/image";
import CTALink from "@/components/CTA/ctaLink";
import LocalizedOfferEndsPrefix from "@/components/PromoBanner/components/LocalizedOfferEndsPrefix";
import PromoBannerTooltip from "@/components/PromoBanner/components/PromoBannerTooltip";
import Icon from "@/components/icons";
import type { CompactV2PromoBannerMonarchProps } from "@/monarch/types/PromoBanner";
import boldTextWithAsterisk from "@/utils/functions/boldTextWithAsterisk";
import { cn } from "@/lib/utils";

type PromoBannerCompactPDPV2Props = Partial<
  Omit<
    CompactV2PromoBannerMonarchProps,
    "cta" | "description" | "backgroundImage"
  >
> & {
  className?: string;
  ctaElement?: React.ReactNode;
  description?: React.ReactNode | string;
  cta?: CompactV2PromoBannerMonarchProps["cta"] & { html_id?: string };
  backgroundImage?: { url: string; altText?: string | null };
};

const THEME_CLASSES = {
  dark: {
    container: "bg-primary-500",
    text: "text-primary-foreground",
    tooltipIconColor: "text-primary-foreground",
    border: "",
  },
  light: {
    container: "bg-putty-200",
    text: "text-card-foreground",
    tooltipIconColor: "text-card-foreground",
    border: "border border-border",
  },
} as const;

export default function PromoBannerCompactPDPV2({
  className,
  cta,
  ctaElement,
  description,
  discountCode,
  endDate,
  endDatePrefix,
  learnMoreLink,
  title,
  tooltip,
  showIcon = false,
  iconType,
  backgroundType = "solid",
  backgroundImage,
  themeVariant = "dark",
}: PromoBannerCompactPDPV2Props) {
  const theme = THEME_CLASSES[themeVariant];

  // Background images are suppressed in light mode (accessibility — insufficient contrast guarantee)
  const hasBackgroundImage =
    backgroundType === "image" &&
    !!backgroundImage?.url &&
    themeVariant === "dark";

  const textColor = cn(theme.text, hasBackgroundImage && "text-white");
  const darkCtaFocusClass =
    themeVariant === "dark"
      ? "focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-0"
      : "";
  // Applies the dark focus ring to any nested button/link inside an externally-
  // provided ctaElement, so callers don't need to know about theme variants.
  const ctaElementFocusWrapperClass =
    themeVariant === "dark"
      ? "contents [&_:is(button,a)]:focus-visible:ring-2 [&_:is(button,a)]:focus-visible:ring-white [&_:is(button,a)]:focus-visible:ring-offset-0"
      : "contents";

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl",
        theme.container,
        theme.border,
        className,
      )}
    >
      {hasBackgroundImage && (
        <NextImage
          src={backgroundImage.url}
          alt=""
          fill
          className="object-cover opacity-90"
          aria-hidden="true"
        />
      )}

      {/* Content sits above the background image in DOM order — no z-index needed */}
      <div className="relative flex flex-col md:flex-row md:items-center gap-6 py-6 px-8">
        {/* Left: icon + title + description */}
        <div className="flex flex-col items-center text-center md:flex-row md:items-center md:text-left gap-6 flex-1">
          {showIcon && iconType && (
            <div className="shrink-0 text-brand-300" aria-hidden="true">
              <Icon
                type={iconType}
                svgProps={{ width: "90", height: "90" }}
                pathProps={{ fill: "currentColor" }}
              />
            </div>
          )}
          <div>
            <h2
              className={cn(
                "elm-h2 xl:text-3xl font-black italic mt-0 mb-3",
                textColor,
              )}
            >
              {title}
            </h2>
            <p className={cn("elm-p3 mb-0", textColor)}>
              {typeof description === "string"
                ? boldTextWithAsterisk(description)
                : description}{" "}
            </p>
          </div>
        </div>

        {/* Right: CTA + date — full-width stacked on mobile, right-aligned column on desktop */}
        <div className="flex flex-col items-center md:justify-center md:self-center gap-4 shrink-0">
          {cta && (
            <CTALink
              type="brand"
              size="xl"
              className={cn(
                "w-full md:w-auto whitespace-nowrap font-normal h-[45px] px-6 py-2.5",
                darkCtaFocusClass,
              )}
              label={cta.label}
              url={cta.url}
              location={cta.location || "Compact PDP Promo Banner"}
              html_id={cta.htmlId || cta.html_id || "compact-pdp-promo-banner"}
            />
          )}
          {!cta && ctaElement && (
            <div className={ctaElementFocusWrapperClass}>{ctaElement}</div>
          )}
          <div className="flex gap-1 items-center">
            <p className={cn("text-xs mb-0 whitespace-nowrap", textColor)}>
              <LocalizedOfferEndsPrefix prefix={endDatePrefix} date={endDate} />
              .
            </p>
            <div className="mb-[2px]">
              <PromoBannerTooltip
                discountCode={discountCode}
                endDate={endDate}
                learnMoreLink={learnMoreLink}
                htmlContent={tooltip?.infoText ?? ""}
                iconColor={
                  hasBackgroundImage ? "text-white" : theme.tooltipIconColor
                }
                size={12}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
